import * as React from 'react';
import Box from '@mui/material/Box';
import { DataGrid, esES, GridColDef } from '@mui/x-data-grid';
import { useGetAlumnos } from '../../hooks/getAlumnos';
import { Button, Chip, FormControlLabel, LinearProgress, Switch, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { AlumnoType, PayloadAlumnoType } from '../../types/types';
import { useContext, useEffect, useState } from 'react';
import CalendarioAsistencia from './CalendarioAsistencia';
import HeaderFab from '../HeaderFab';
import AlumnosDetail from './AlumnosDetail';
import { useAutoPostAlumno } from '../../hooks/usePostAlumno';
import { useDebouncedCallback } from 'use-debounce';
import { useQueryClient } from 'react-query';
import { UserContext } from '../../contexts/userContext';
import DeudaReportDialog from '../DeudaReport/DeudaReportDialog';
import dayjs from 'dayjs';
import CustomToolbar from '../CustomToolbar/CustomToolbar';

export default function AlumnosList(props: { filter?: string[] }) {
  const { filter } = props;
  const { user } = useContext(UserContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [alumnoEnCalendario, setAlumnoEnCalendario] = useState<AlumnoType | null>(null);
  const [alumnoAEditar, setAlumnoAEditar] = useState<AlumnoType | undefined>();
  const [verInactivos, setVerInactivos] = useState(false);
  const [verHistorico, setVerHistorico] = useState(false);
  const [verDetalleAlumno, setVerDetalleAlumno] = useState(false);
  const [alumnoDeuda, setAlumnoDeuda] = useState<AlumnoType | null>(null);

  const alumnos = useGetAlumnos(user, verInactivos, verHistorico);

  const [ mutableAlumno, setMutableAlumno ] = useState<PayloadAlumnoType | undefined>();
  useAutoPostAlumno({ alumno: mutableAlumno }, user);

  const debouncedRefresh = useDebouncedCallback(
    () => {alumnos.refetch();},
    1000,
  );

  const queryClient = useQueryClient();
  const qc = queryClient.isFetching('postAlumnoApi');
  useEffect(() => {
    debouncedRefresh();
  }, [qc]);

  if (!user?._id) return null;

  const columns: GridColDef[] = [
    {
      field: 'nombre-apellido',
      headerName: 'Nombre y Apellido',
      headerClassName: 'table-headers',
      flex: 1,
      renderHeader: () => <strong>Apellido y Nombre</strong>,
      valueGetter: (params) =>`${params.row.apellido} ${params.row.name}
      ${params.row.becado ? ' becado' : ''} ${params.row.descuentoHermano ? ' descuento' : ''}`,
      renderCell: (params) => {
        const alumno = params.row as AlumnoType;
        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
            flexDirection: isMobile ? 'column' : undefined,
            padding: 0,
          }}>
            <Button
              onClick={() => {
                setAlumnoAEditar(alumno);
                setVerDetalleAlumno(true);
              }}
              variant="text"
              color="primary"
              style={{
                whiteSpace: 'pre-wrap',
                textAlign: 'left',
                alignSelf: 'flex-start',
                padding: 0,
              }}
            >
              {`${params.row.apellido} ${params.row.name}`}
            </Button>
            <div style={{
              alignSelf: 'flex-start',
              textAlign: isMobile ? 'start' : 'end',
              padding: 0,
              margin: 0,
              width: 'min-content',
              marginBottom: isMobile ? 6 : 0,
            }}>
              {alumno.becado && <Chip color="success" size='small' label='Becado' style={{ zoom: 0.8, padding: 2 }}/>}
              {alumno.descuentoHermano && <Chip color="success" size='small' label='Descuento' style={{ zoom: 0.8, padding: 2 }}/>}
            </div>
          </div>
        );
      },
    },
    {
      field: 'dni',
      headerName: 'DNI',
      headerClassName: 'table-headers',
      flex: 1,
      renderHeader: () => (<strong>DNI</strong>),
      hide: isMobile,
    },
    {
      field: 'grupo',
      headerName: 'Grupo',
      headerClassName: 'table-headers',
      flex: 1,
      renderHeader: () => (<strong>Grupo</strong>),
      renderCell: (params) => (
        <span
          style={{
            whiteSpace: 'pre-wrap',
            textAlign: 'left',
          }}
        >
          {params.row.grupo}
        </span>
      ),
    },
    {
      field: 'asistencia',
      headerName: 'Inasistencias',
      headerClassName: 'table-headers',
      flex: 1,
      sortingOrder: ['desc'],
      sortComparator: (v1, v2) => {
        const inasistencias1 = v1?.filter((a:any) => !a?.asistencia).length ?? 0;
        const inasistencias2 = v2?.filter((a:any) => !a?.asistencia).length ?? 0;
        return inasistencias1 - inasistencias2;
      },
      renderHeader: () => (<strong>Inasistencias</strong>),
      renderCell: (params) => {
        const alumno = params.row as AlumnoType;
        const inasistenciasTotales = alumno.asistencia?.filter((a) => !a?.asistencia).length ?? 0;
        const inasistenciasDelAñoActual = alumno.asistencia?.filter(a => {
          const esInasistencia = !a?.asistencia;
          const esDelAñoActual = dayjs(a?.dia).year() === dayjs().year();
          return esInasistencia && esDelAñoActual;
        }).length ?? 0;

        const inasistencias = verHistorico ? inasistenciasTotales : inasistenciasDelAñoActual;

        return (
          <Button
            onClick={() => setAlumnoEnCalendario(alumno)}
            startIcon={<Visibility/>}
            variant='contained'
            color={inasistencias === 0 ? 'info' : 'warning'}
            size="small"
            style={{ margin: 8 }}
          >
            {inasistencias}
          </Button>
        );
      },
    },
    {
      field: 'cuotasAdeudadas',
      headerName: 'Deuda',
      headerClassName: 'table-headers',
      flex: 1,
      hide: !user?.roles?.includes('registrar pagos'),
      renderHeader: () => (<strong>Deuda</strong>),
      valueGetter: (params) => params.row.cuotasAdeudadas?.n,
      renderCell: (params) => {
        const alumno = params.row as AlumnoType;
        const cuotasAdeudadas = params.row.cuotasAdeudadas?.n < 0 ? 0 : params.row.cuotasAdeudadas?.n;
        return (
          <>
            <Chip
              onClick={() => {setAlumnoDeuda(alumno);}}
              color={cuotasAdeudadas ? 'error' : 'success'}
              label={`${cuotasAdeudadas} cuotas`}
            />
          </>
        );
      },
    },
    {
      field: 'inactivo',
      headerName: '',
      headerClassName: 'table-headers',
      flex: 1,
      renderHeader: () => '',
      renderCell: (params) => {
        if (!alumnos.data) return;
        const alumno = alumnos.data.find((a) => a.dni === params.row.dni);
        if (!alumno) return;
        if (alumno.grupo) return (
          <Typography fontSize={10}>
            Alumnos asignados a grupos no pueden inactivarse.
          </Typography>
        );
        return (
          <FormControlLabel
            control={<Switch
              color="error"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (alumno) {
                  alumno.inactivo = event.target.checked;
                  setMutableAlumno(alumno);
                }
              }}
            />}
            checked={alumno.inactivo}
            label="Inactivo"
          />
        );
      },
      hide: !verInactivos && !filter,
    },
    { field: 'direccion', headerName: 'Direccion', hide: true },
    { field: 'celmama', headerName: 'Celular de la madre', hide: true },
    { field: 'celpapa', headerName: 'Celular del padre', hide: true },
    { field: 'fechanac',
      headerName: 'Fecha Nacimiento',
      valueGetter: (params) => dayjs(params.row.fechanac).format('DD/MM/YYYY'),
      hide: true,
    },
  ];

  if (alumnos.isLoading || !alumnos.data) return <LinearProgress />;

  let filteredAlumnos = alumnos.data;

  if (filter) filteredAlumnos = filteredAlumnos.filter((a) => filter?.includes(a.dni));

  let conteinerHeight:string | undefined = 'calc(100vh - 115px)';
  if (isMobile) conteinerHeight = 'calc(100vh - 60px)';

  return (
    <Box sx={{ height: conteinerHeight, width: 1 }}>
      <AlumnosDetail
        alumno={alumnoAEditar}
        open={verDetalleAlumno}
        onClose={() => {
          setAlumnoAEditar(undefined);
          setVerDetalleAlumno(false);
        }}
        setOpen={setVerDetalleAlumno}
        refetchLista={()=>{alumnos.refetch();}}
        lista={alumnos.data}
      />
      <CalendarioAsistencia alumno={alumnoEnCalendario} setAlumno={setAlumnoEnCalendario} refetchLista={()=>{alumnos.refetch();}} />
      <DataGrid
        getRowHeight={() => 'auto'}
        headerHeight={30}
        rows={filteredAlumnos}
        columns={columns}
        getRowId={(row) =>  row._id ?? row.dni}
        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        components={{ Toolbar: (toolbarProps) =>
          <CustomToolbar
            {...toolbarProps}
            rows={filteredAlumnos}
            title={'Lista de Alumnos'}
            columnMapping={{
              apellido: 'Apellido',
              name: 'Nombre',
              dni: 'DNI',
              direccion: 'Direccion',
              celmama: 'Cel mama',
              celpapa: 'Cel papa',
              fechanac: 'Fecha de nacimiento',
              federado: 'Federado',
              grupo: 'Grupo',
              categoria: 'Categoria',
              nivel:'Nivel',
            }}
          /> }}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        disableSelectionOnClick
        initialState={{
          sorting: {
            sortModel: [{ field: 'nombre-apellido', sort: 'asc' }],
          },
        }}
      />
      {!filter && <HeaderFab
        title='Agregar Alumno'
        onclick={()=>{
          setAlumnoAEditar(undefined);
          setVerDetalleAlumno(true);
        }}
      />}
      {!isMobile && user?.roles?.includes('editar datos') && !filter && <FormControlLabel
        style={{
          position: 'fixed',
          top: 90,
          right: 280,
          zIndex: 1200,
        }}
        control={<Switch
          color="default"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setVerInactivos(event.target.checked);
          }}
        />}
        checked={verInactivos}
        label="Ver Inactivos"
      />}
      {!isMobile && user?.roles?.includes('editar datos') && !filter && <FormControlLabel
        style={{
          position: 'fixed',
          top: 90,
          right: 450,
          zIndex: 1200,
        }}
        control={<Switch
          color= {verHistorico ? 'error' : 'default'}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setVerHistorico(event.target.checked);
          }}
        />}
        checked={verHistorico}
        label="Ver Historico"
      />}
      <DeudaReportDialog
        alumno={alumnoDeuda}
        onClose={() => {setAlumnoDeuda(null);}}
        refetchLista={()=>{alumnos.refetch();}}
        verHistorico={verHistorico}
      />
    </Box>
  );
}
